import { useMemo } from 'react'

import { useModals, useNumericParams, useSetState } from '@domain-group/react-hooks'

import { ModalContext } from '@domain-group/suit-theme'

import PageContext from '@contexts/pageContext'

import handleCallbackAction from '@functions/handleCallbackAction'
import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import type {
  // @ts-expect-error TS2614
  ManageSlackWebhookSettingsParams,
  // @ts-expect-error TS2614
  ManageSystemIntegationsParams,
  ShowCreateOrEditDataStoreItemKeyModalParams,
  ShowManageSystemIntegrationsModalParams,
  ShowManageSystemMaintenanceModalParams,
  ShowSlackWebhookSettingsModalParams,
  ShowCreateOrEditQualityControlCategoryParams,
} from '@hooks/useSystem'

import CreateOrEditDataStoreItemKeyModal from '@modals/CreateOrEditDataStoreItemKeyModal'
import CreateOrEditTemplateQualityControlCategoryModal from '@modals/CreateOrEditTemplateQualityControlCategoryModal'
import ManageSystemIntegrationsModal from '@modals/ManageSystemIntegrationsModal'
import ManageSystemMaintenanceModal from '@modals/ManageSystemMaintenanceModal'
import SlackWebhookSettingsModal from '@modals/SlackWebhookSettingsModal'

import PageContent from './components/PageContent'

const defaultState = {
  showCreateOrEditDataStoreItemKeyModal: false,
  showCreateOrEditTemplateQualityControlCategoryModal: false,
  showManageSystemIntegrationsModal: false,
  showManageSystemMaintenanceModal: false,
  showSlackWebhookSettingsModal: false,
}

const callbacks = (
  component: keyof typeof componentCallbacks,
  setState: (state: Partial<typeof defaultState>) => void,
) => {
  const componentCallbacks = {
    CreateOrEditDataStoreItemKeyModal: {
      closeModal: () => setState({ showCreateOrEditDataStoreItemKeyModal: false }),
      updateSystem: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    CreateOrEditTemplateQualityControlCategoryModal: {
      closeModal: () => setState({ showCreateOrEditTemplateQualityControlCategoryModal: false }),
      updateSystem: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    ManageSystemIntegrationsModal: {
      closeModal: () => setState({ showManageSystemIntegrationsModal: false }),
      updateSystem: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    ManageSystemMaintenanceModal: {
      closeModal: () => setState({ showManageSystemMaintenanceModal: false }),
      updateSystem: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    SlackWebhookSettingsModal: {
      closeModal: () => setState({ showSlackWebhookSettingsModal: false }),
      updateSystem: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
  }

  return componentCallbacks[component] || {}
}

const Details = () => {
  const [state, setState] = useSetState(defaultState)
  const {
    showCreateOrEditDataStoreItemKeyModal,
    showCreateOrEditTemplateQualityControlCategoryModal,
    showManageSystemIntegrationsModal,
    showManageSystemMaintenanceModal,
    showSlackWebhookSettingsModal,
  } = state

  const { systemId } = useNumericParams()

  const modalContext = useModals({ setState })
  const {
    callbacks: { setModalData },
  } = modalContext

  const pageContext = useMemo(() => ({
    callbacks: {
      showCreateOrEditDataStoreItemKeyModal: (payload: ShowCreateOrEditDataStoreItemKeyModalParams) => {
        setModalData('CreateOrEditDataStoreItemKeyModal', payload)
        setState({ showCreateOrEditDataStoreItemKeyModal: true })
      },
      showCreateOrEditTemplateQualityControlCategoryModal: (payload: ShowCreateOrEditQualityControlCategoryParams) => {
        setModalData('CreateOrEditTemplateQualityControlCategoryModal', payload)
        setState({ showCreateOrEditTemplateQualityControlCategoryModal: true })
      },
      showManageSystemIntegrationsModal: (payload: ShowManageSystemIntegrationsModalParams) => {
        setModalData('ManageSystemIntegrationsModal', payload)
        setState({ showManageSystemIntegrationsModal: true })
      },
      showManageSystemMaintenanceModal: (payload: ShowManageSystemMaintenanceModalParams) => {
        setModalData('ManageSystemMaintenanceModal', payload)
        setState({ showManageSystemMaintenanceModal: true })
      },
      showSlackWebhookSettingsModal: (payload: ShowSlackWebhookSettingsModalParams) => {
        setModalData('SlackWebhookSettingsModal', payload)
        setState({ showSlackWebhookSettingsModal: true })
      },
      updateSystem: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
  }), [])

  return (
    <PageContext.Provider value={pageContext}>
      <ModalContext.Provider value={modalContext}>
        <PageContent systemId={systemId} />

        <CreateOrEditDataStoreItemKeyModal
          // @ts-expect-error TS2322
          callbacks={callbacks('CreateOrEditDataStoreItemKeyModal', setState)}
          showModal={showCreateOrEditDataStoreItemKeyModal}
        />

        <CreateOrEditTemplateQualityControlCategoryModal
          // @ts-expect-error TS2322
          callbacks={callbacks('CreateOrEditTemplateQualityControlCategoryModal', setState)}
          showModal={showCreateOrEditTemplateQualityControlCategoryModal}
        />

        <ManageSystemIntegrationsModal
          callbacks={callbacks('ManageSystemIntegrationsModal', setState)}
          showModal={showManageSystemIntegrationsModal}
        />

        <ManageSystemMaintenanceModal
          callbacks={callbacks('ManageSystemMaintenanceModal', setState)}
          showModal={showManageSystemMaintenanceModal}
        />

        <SlackWebhookSettingsModal
          callbacks={callbacks('SlackWebhookSettingsModal', setState)}
          showModal={showSlackWebhookSettingsModal}
        />
      </ModalContext.Provider>
    </PageContext.Provider>
  )
}

export default Details
